import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Lock,
  Loader2,
  RefreshCw,
  ShieldAlert,
  CheckCircle2,
  AlertTriangle,
  FileWarning,
  Layers,
  Building2,
  Clock,
  ListChecks,
  ChevronDown,
  ChevronRight,
  RotateCw,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PlatformLayout } from "./platform-layout";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type MigrationSource =
  | "template-migration"
  | "instance-migration"
  | "social-media-asset-migration"
  | "talent-photo-migration"
  | "newsletter-cover-migration"
  | "unknown";

type OutcomeCounts = {
  total: number;
  reEncrypted: number;
  alreadyEncrypted: number;
  notFound: number;
  scanFailed: number;
  downloadFailed: number;
  exception: number;
};

type SourceBreakdown = OutcomeCounts & { source: MigrationSource };

type WorkspaceSummary = {
  workspaceId: string;
  workspaceName: string;
  totals: OutcomeCounts;
  bySource: SourceBreakdown[];
};

type FailureRow = {
  id: string;
  createdAt: string;
  workspaceId: string;
  workspaceName: string;
  source: MigrationSource;
  outcome:
    | "not_found"
    | "scan_failed"
    | "download_failed"
    | "exception";
  threat: string | null;
  errorMessage: string | null;
  fileName: string | null;
  fileSize: number | null;
  objectKey: string | null;
  documentInstanceId: string | null;
  templateId: string | null;
  clientId: string | null;
  sourceRecordId: string | null;
};

type RetryResultRow = {
  failureLogId: string;
  workspaceId: string;
  source: MigrationSource;
  recordId: string | null;
  outcome:
    | "re_encrypted"
    | "already_encrypted"
    | "not_found"
    | "scan_failed"
    | "errored"
    | "source_missing"
    | "non_bucket_path"
    | "unknown_source";
  errorMessage?: string;
};

type RetryResponse = {
  attempted: number;
  succeeded: number;
  results: RetryResultRow[];
};

type EncryptionMigrationResponse = {
  outcomes: OutcomeCounts & { dryRunSeen: number };
  byWorkspace: WorkspaceSummary[];
  failures: FailureRow[];
  lastMigrationAt: string | null;
  failureLimit: number;
};

type RunOutcomeCounts = OutcomeCounts & { failed: number };

type RunSourceBreakdown = RunOutcomeCounts & { source: MigrationSource };

type MigrationRun = {
  runId: string;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  isDryRun: boolean;
  counts: RunOutcomeCounts;
  bySource: RunSourceBreakdown[];
  failureLogIds: string[];
  failureLogCount: number;
};

type EncryptionMigrationRunsResponse = {
  runs: MigrationRun[];
  gapMinutes: number;
  runLimit: number;
  rowLimit: number;
  rowsScanned: number;
};

const SOURCE_LABEL: Record<MigrationSource, string> = {
  "template-migration": "Templates",
  "instance-migration": "Instances",
  "social-media-asset-migration": "Social media assets",
  "talent-photo-migration": "Talent photos",
  "newsletter-cover-migration": "Newsletter covers",
  unknown: "Unknown source",
};

const OUTCOME_LABEL: Record<FailureRow["outcome"], string> = {
  not_found: "Missing from bucket",
  scan_failed: "Virus scan failed",
  download_failed: "Download failed",
  exception: "Migration error",
};

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${ms} ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const remSec = seconds % 60;
  if (mins < 60) return remSec ? `${mins}m ${remSec}s` : `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remMin = mins % 60;
  return remMin ? `${hours}h ${remMin}m` : `${hours}h`;
}

function StatTile({
  label,
  value,
  tone,
  icon: Icon,
  testid,
}: {
  label: string;
  value: number;
  tone: "default" | "good" | "warn" | "bad";
  icon: typeof CheckCircle2;
  testid: string;
}) {
  const toneClass =
    tone === "good"
      ? "text-emerald-600"
      : tone === "warn"
        ? "text-amber-600"
        : tone === "bad"
          ? "text-red-600"
          : "text-foreground";
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
          <Icon className={`h-3.5 w-3.5 ${toneClass}`} />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={`text-2xl font-semibold ${toneClass}`}
          data-testid={testid}
        >
          {value.toLocaleString()}
        </div>
      </CardContent>
    </Card>
  );
}

function SourceRow({ breakdown }: { breakdown: SourceBreakdown }) {
  const failures =
    breakdown.notFound +
    breakdown.scanFailed +
    breakdown.downloadFailed +
    breakdown.exception;
  return (
    <tr
      className="border-t text-sm"
      data-testid={`row-source-${breakdown.source}`}
    >
      <td className="px-3 py-2">
        <Badge variant="outline" className="font-normal">
          {SOURCE_LABEL[breakdown.source]}
        </Badge>
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {breakdown.total.toLocaleString()}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-emerald-600">
        {breakdown.reEncrypted.toLocaleString()}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {breakdown.alreadyEncrypted.toLocaleString()}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-amber-600">
        {breakdown.notFound.toLocaleString()}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-red-600">
        {breakdown.scanFailed.toLocaleString()}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-red-600">
        {(breakdown.downloadFailed + breakdown.exception).toLocaleString()}
      </td>
      <td className="px-3 py-2 text-right tabular-nums font-medium">
        {failures.toLocaleString()}
      </td>
    </tr>
  );
}

function WorkspaceCard({
  summary,
  onRetryWorkspace,
  isRetryingWorkspace,
}: {
  summary: WorkspaceSummary;
  onRetryWorkspace: (workspaceId: string) => void;
  isRetryingWorkspace: boolean;
}) {
  const totalFailures =
    summary.totals.notFound +
    summary.totals.scanFailed +
    summary.totals.downloadFailed +
    summary.totals.exception;
  return (
    <Card data-testid={`card-workspace-${summary.workspaceId}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-3 text-base">
          <span className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span data-testid={`text-workspace-name-${summary.workspaceId}`}>
              {summary.workspaceName}
            </span>
          </span>
          <span className="flex items-center gap-2">
            {totalFailures > 0 ? (
              <Badge
                variant="destructive"
                data-testid={`badge-workspace-failures-${summary.workspaceId}`}
              >
                {totalFailures.toLocaleString()} failed
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="border-emerald-500 text-emerald-600"
                data-testid={`badge-workspace-clean-${summary.workspaceId}`}
              >
                <CheckCircle2 className="mr-1 h-3 w-3" />
                Clean
              </Badge>
            )}
            <Badge variant="secondary">
              {summary.totals.total.toLocaleString()} files
            </Badge>
            {totalFailures > 0 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRetryWorkspace(summary.workspaceId)}
                disabled={isRetryingWorkspace}
                data-testid={`button-retry-workspace-${summary.workspaceId}`}
              >
                {isRetryingWorkspace ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <RotateCw className="h-3.5 w-3.5 mr-1.5" />
                )}
                Retry all failures
              </Button>
            ) : null}
          </span>
        </CardTitle>
        <CardDescription className="text-xs font-mono">
          {summary.workspaceId}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Source</th>
                <th className="px-3 py-2 text-right font-medium">Total</th>
                <th className="px-3 py-2 text-right font-medium">Encrypted</th>
                <th className="px-3 py-2 text-right font-medium">
                  Already enc.
                </th>
                <th className="px-3 py-2 text-right font-medium">Missing</th>
                <th className="px-3 py-2 text-right font-medium">Scan fail</th>
                <th className="px-3 py-2 text-right font-medium">Errored</th>
                <th className="px-3 py-2 text-right font-medium">Failed</th>
              </tr>
            </thead>
            <tbody>
              {summary.bySource.map((b) => (
                <SourceRow key={b.source} breakdown={b} />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function RunRow({
  run,
  onSelectFailure,
}: {
  run: MigrationRun;
  onSelectFailure?: (failureLogId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const failed = run.counts.failed;
  const skipped = run.counts.alreadyEncrypted;
  return (
    <div
      className="rounded-md border bg-card"
      data-testid={`row-run-${run.runId}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5">
        <div className="flex items-start gap-3 min-w-0">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-0.5 text-muted-foreground hover-elevate active-elevate-2 rounded p-0.5"
            aria-label={expanded ? "Collapse run details" : "Expand run details"}
            data-testid={`button-toggle-run-${run.runId}`}
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="text-sm font-medium"
                data-testid={`text-run-started-${run.runId}`}
              >
                {formatTimestamp(run.startedAt)}
              </span>
              {run.isDryRun ? (
                <Badge
                  variant="outline"
                  className="border-amber-500 text-amber-600"
                  data-testid={`badge-run-dry-${run.runId}`}
                >
                  Dry-run preview
                </Badge>
              ) : null}
              <span className="text-xs text-muted-foreground">
                · {formatDuration(run.durationMs)}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
              <span data-testid={`text-run-scanned-${run.runId}`}>
                <span className="text-foreground font-medium">
                  {run.counts.total.toLocaleString()}
                </span>{" "}
                scanned
              </span>
              <span
                className="text-emerald-600"
                data-testid={`text-run-encrypted-${run.runId}`}
              >
                <span className="font-medium">
                  {run.counts.reEncrypted.toLocaleString()}
                </span>{" "}
                re-encrypted
              </span>
              <span data-testid={`text-run-skipped-${run.runId}`}>
                <span className="font-medium">{skipped.toLocaleString()}</span>{" "}
                skipped (already encrypted)
              </span>
              <span
                className={
                  failed > 0
                    ? "text-red-600 font-medium"
                    : "text-muted-foreground"
                }
                data-testid={`text-run-failed-${run.runId}`}
              >
                {failed.toLocaleString()} failed
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {failed > 0 ? (
            <Badge
              variant="destructive"
              data-testid={`badge-run-failed-${run.runId}`}
            >
              {failed.toLocaleString()} failed
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="border-emerald-500 text-emerald-600"
              data-testid={`badge-run-clean-${run.runId}`}
            >
              <CheckCircle2 className="mr-1 h-3 w-3" />
              Clean
            </Badge>
          )}
        </div>
      </div>
      {expanded ? (
        <div
          className="border-t px-3 py-3 space-y-3 bg-muted/20"
          data-testid={`detail-run-${run.runId}`}
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <DetailStat label="Re-encrypted" value={run.counts.reEncrypted} tone="good" />
            <DetailStat label="Already encrypted" value={run.counts.alreadyEncrypted} />
            <DetailStat label="Missing from bucket" value={run.counts.notFound} tone={run.counts.notFound > 0 ? "warn" : "default"} />
            <DetailStat label="Failed virus scan" value={run.counts.scanFailed} tone={run.counts.scanFailed > 0 ? "bad" : "default"} />
            <DetailStat label="Download failed" value={run.counts.downloadFailed} tone={run.counts.downloadFailed > 0 ? "bad" : "default"} />
            <DetailStat label="Exception" value={run.counts.exception} tone={run.counts.exception > 0 ? "bad" : "default"} />
          </div>

          {run.bySource.length > 0 ? (
            <div className="rounded-md border overflow-hidden bg-background">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Source</th>
                    <th className="px-3 py-2 text-right font-medium">Scanned</th>
                    <th className="px-3 py-2 text-right font-medium">Re-enc.</th>
                    <th className="px-3 py-2 text-right font-medium">Already enc.</th>
                    <th className="px-3 py-2 text-right font-medium">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {run.bySource.map((b) => (
                    <tr
                      key={b.source}
                      className="border-t"
                      data-testid={`row-run-source-${run.runId}-${b.source}`}
                    >
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="font-normal">
                          {SOURCE_LABEL[b.source]}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {b.total.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-600">
                        {b.reEncrypted.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {b.alreadyEncrypted.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-600">
                        {b.failed.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {failed > 0 ? (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1.5">
                Failure audit log rows
                {run.failureLogCount > run.failureLogIds.length ? (
                  <span className="ml-1 text-muted-foreground/70">
                    (showing {run.failureLogIds.length} of {run.failureLogCount})
                  </span>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {run.failureLogIds.map((id) => (
                  <a
                    key={id}
                    href={`#failure-${id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      onSelectFailure?.(id);
                    }}
                    className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-muted text-foreground/80 border hover-elevate active-elevate-2 cursor-pointer underline-offset-2 hover:underline"
                    data-testid={`link-run-failure-id-${id}`}
                    title="Click to drill into this document_access_log entry in the Failed migrations table below"
                  >
                    {id}
                  </a>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Each id is a{" "}
                <code className="font-mono text-[10px]">document_access_log</code>{" "}
                row — click to jump to it in the{" "}
                <span className="font-medium">Failed migrations</span> table
                below.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function DetailStat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "good" | "warn" | "bad";
}) {
  const toneClass =
    tone === "good"
      ? "text-emerald-600"
      : tone === "warn"
        ? "text-amber-600"
        : tone === "bad"
          ? "text-red-600"
          : "text-foreground";
  return (
    <div className="rounded-md border bg-background px-2.5 py-1.5">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${toneClass}`}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function resolveRowRecordId(row: FailureRow): string | null {
  switch (row.source) {
    case "template-migration":
      return row.templateId;
    case "instance-migration":
      return row.documentInstanceId;
    case "social-media-asset-migration":
    case "talent-photo-migration":
    case "newsletter-cover-migration":
      return row.sourceRecordId;
    default:
      return null;
  }
}

function FailureRowView({
  row,
  highlighted,
  onRetry,
  isRetrying,
}: {
  row: FailureRow;
  highlighted: boolean;
  onRetry: (failureId: string) => void;
  isRetrying: boolean;
}) {
  const recordId = resolveRowRecordId(row);
  const canRetry = recordId !== null && row.source !== "unknown";
  return (
    <tr
      id={`failure-${row.id}`}
      className={`border-t text-xs align-top transition-colors ${
        highlighted
          ? "bg-amber-100/70 dark:bg-amber-950/40"
          : ""
      }`}
      data-testid={`row-failure-${row.id}`}
      data-highlighted={highlighted ? "true" : undefined}
    >
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="text-muted-foreground">
          {formatTimestamp(row.createdAt)}
        </div>
        <a
          href={`#failure-${row.id}`}
          className="block font-mono text-[10px] text-muted-foreground/80 hover:text-foreground hover:underline mt-1 break-all max-w-[180px]"
          data-testid={`link-failure-audit-id-${row.id}`}
          title="document_access_log.id — anchor link for this audit row"
        >
          log:{row.id}
        </a>
      </td>
      <td className="px-3 py-2">
        <Badge variant="destructive" data-testid={`badge-failure-outcome-${row.id}`}>
          {OUTCOME_LABEL[row.outcome]}
        </Badge>
        <div className="mt-1 text-muted-foreground">
          {SOURCE_LABEL[row.source]}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="font-medium">{row.workspaceName}</div>
        <div className="font-mono text-muted-foreground break-all max-w-[180px]">
          {row.workspaceId}
        </div>
      </td>
      <td className="px-3 py-2">
        {row.fileName ? (
          <div className="font-medium truncate max-w-[260px]">
            {row.fileName}
          </div>
        ) : null}
        {row.objectKey ? (
          <div className="font-mono text-muted-foreground truncate max-w-[260px]">
            {row.objectKey}
          </div>
        ) : null}
        <div className="text-muted-foreground mt-0.5">
          {formatBytes(row.fileSize)}
        </div>
      </td>
      <td className="px-3 py-2">
        {row.documentInstanceId ? (
          <div className="font-mono text-muted-foreground break-all max-w-[200px]">
            doc:{row.documentInstanceId}
          </div>
        ) : null}
        {row.templateId ? (
          <div className="font-mono text-muted-foreground break-all max-w-[200px]">
            tpl:{row.templateId}
          </div>
        ) : null}
        {row.sourceRecordId &&
        row.source !== "template-migration" &&
        row.source !== "instance-migration" ? (
          <div className="font-mono text-muted-foreground break-all max-w-[200px]">
            id:{row.sourceRecordId}
          </div>
        ) : null}
        {row.clientId ? (
          <div className="font-mono text-muted-foreground break-all max-w-[200px]">
            client:{row.clientId}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2">
        {row.threat ? (
          <Badge variant="destructive" className="mb-1">
            {row.threat}
          </Badge>
        ) : null}
        {row.errorMessage ? (
          <div
            className="text-muted-foreground break-words max-w-[300px]"
            data-testid={`text-failure-message-${row.id}`}
          >
            {row.errorMessage}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onRetry(row.id)}
          disabled={!canRetry || isRetrying}
          title={
            !canRetry
              ? "Originating row id is unknown — retry not available"
              : undefined
          }
          data-testid={`button-retry-failure-${row.id}`}
        >
          {isRetrying ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <RotateCw className="h-3.5 w-3.5 mr-1.5" />
          )}
          Retry
        </Button>
      </td>
    </tr>
  );
}


export function EncryptionMigrationContent() {
  const { toast } = useToast();
  const [failureLimit, setFailureLimit] = useState(200);
  const [highlightedFailureId, setHighlightedFailureId] = useState<string | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  const [pendingFailureId, setPendingFailureId] = useState<string | null>(null);
  const [pendingWorkspaceId, setPendingWorkspaceId] = useState<string | null>(null);
  const queryKey = [
    "/api/platform/encryption-migration",
    { failureLimit },
  ] as const;

  const { data, isLoading, isFetching, error, refetch } =
    useQuery<EncryptionMigrationResponse>({
      queryKey,
      queryFn: async () => {
        const res = await fetch(
          `/api/platform/encryption-migration?failureLimit=${failureLimit}`,
          { credentials: "include" },
        );
        if (!res.ok) {
          throw new Error(
            `Failed to load encryption-migration summary: ${res.status}`,
          );
        }
        return res.json();
      },
    });

  const {
    data: runsData,
    isLoading: runsLoading,
    isFetching: runsFetching,
    error: runsError,
    refetch: refetchRuns,
  } = useQuery<EncryptionMigrationRunsResponse>({
    queryKey: ["/api/platform/encryption-migration/runs"],
    queryFn: async () => {
      const res = await fetch("/api/platform/encryption-migration/runs", {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error(
          `Failed to load encryption-migration runs: ${res.status}`,
        );
      }
      return res.json();
    },
  });

  const handleSelectFailure = useCallback(
    (failureLogId: string) => {
      const isInWindow = (data?.failures ?? []).some(
        (f) => f.id === failureLogId,
      );
      if (!isInWindow) {
        toast({
          title: "Audit row outside the loaded window",
          description: `document_access_log id ${failureLogId} isn't in the latest ${
            data?.failureLimit ?? failureLimit
          } failed migrations. Use "Load more" below to expand the window.`,
        });
        return;
      }
      setHighlightedFailureId(failureLogId);
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
      requestAnimationFrame(() => {
        const el = document.getElementById(`failure-${failureLogId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
      highlightTimerRef.current = setTimeout(() => {
        setHighlightedFailureId((current) =>
          current === failureLogId ? null : current,
        );
      }, 3500);
    },
    [data?.failures, data?.failureLimit, failureLimit, toast],
  );

  const retryMutation = useMutation<
    RetryResponse,
    Error,
    { failureIds?: string[]; workspaceId?: string }
  >({
    mutationFn: async (body) => {
      const res = await apiRequest(
        "POST",
        "/api/platform/encryption-migration/retry",
        body,
      );
      return (await res.json()) as RetryResponse;
    },
    onSuccess: (result) => {
      const unresolved = result.attempted - result.succeeded;
      toast({
        title: unresolved === 0 ? "Retry complete" : "Retry finished with errors",
        description:
          result.attempted === 0
            ? "Nothing to retry — these failures may already be resolved."
            : `${result.succeeded.toLocaleString()} of ${result.attempted.toLocaleString()} files resolved (re-encrypted or already encrypted)${unresolved > 0 ? `, ${unresolved.toLocaleString()} still failing` : ""}.`,
        variant: unresolved === 0 ? "default" : "destructive",
      });
      void queryClient.invalidateQueries({
        queryKey: ["/api/platform/encryption-migration"],
      });
    },
    onError: (err) => {
      toast({
        title: "Retry failed",
        description: err.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setPendingFailureId(null);
      setPendingWorkspaceId(null);
    },
  });

  const handleRetryRow = (failureId: string) => {
    setPendingFailureId(failureId);
    retryMutation.mutate({ failureIds: [failureId] });
  };
  const handleRetryWorkspace = (workspaceId: string) => {
    setPendingWorkspaceId(workspaceId);
    retryMutation.mutate({ workspaceId });
  };

  const successCount = useMemo(
    () => data?.outcomes.reEncrypted ?? 0,
    [data],
  );
  const totalCount = useMemo(() => data?.outcomes.total ?? 0, [data]);
  const failureCount = useMemo(
    () =>
      (data?.outcomes.notFound ?? 0) +
      (data?.outcomes.scanFailed ?? 0) +
      (data?.outcomes.downloadFailed ?? 0) +
      (data?.outcomes.exception ?? 0),
    [data],
  );

  return (
    <>
      <div className="p-3 sm:p-6 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-500" />
              File Encryption Migration
            </h1>
            <p className="text-muted-foreground">
              Back-fill progress for{" "}
              <span className="font-mono text-xs">
                scripts/encrypt-existing-attachments.ts
              </span>
              . One row per attempted file in the document audit log.
            </p>
            {data?.lastMigrationAt ? (
              <p
                className="text-xs text-muted-foreground mt-1 flex items-center gap-1"
                data-testid="text-last-run-at"
              >
                <Clock className="h-3 w-3" />
                Last migration row written {formatTimestamp(
                  data.lastMigrationAt,
                )}
              </p>
            ) : (
              <p
                className="text-xs text-muted-foreground mt-1"
                data-testid="text-last-run-at"
              >
                No migration runs recorded yet — kick off the back-fill from
                the deploy console.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                refetch();
                refetchRuns();
              }}
              disabled={isFetching || runsFetching}
              data-testid="button-refresh-encryption-migration"
            >
              {isFetching || runsFetching ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              )}
              Refresh
            </Button>
          </div>
        </div>

        {data?.outcomes.dryRunSeen ? (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-2"
            data-testid="banner-dry-run"
          >
            <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              {data.outcomes.dryRunSeen.toLocaleString()} migration audit rows
              from <span className="font-mono">--dry</span> runs were ignored
              for the counts below. They are recorded in the audit log for
              traceability but represent a preview, not a real outcome.
            </div>
          </div>
        ) : null}

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-8 text-destructive text-sm">
              {(error as Error).message}
            </CardContent>
          </Card>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              <StatTile
                label="Files attempted"
                value={totalCount}
                tone="default"
                icon={Layers}
                testid="stat-total"
              />
              <StatTile
                label="Encrypted"
                value={successCount}
                tone="good"
                icon={CheckCircle2}
                testid="stat-encrypted"
              />
              <StatTile
                label="Already encrypted"
                value={data.outcomes.alreadyEncrypted}
                tone="default"
                icon={Lock}
                testid="stat-already-encrypted"
              />
              <StatTile
                label="Missing from bucket"
                value={data.outcomes.notFound}
                tone="warn"
                icon={FileWarning}
                testid="stat-missing"
              />
              <StatTile
                label="Failed virus scan"
                value={data.outcomes.scanFailed}
                tone="bad"
                icon={ShieldAlert}
                testid="stat-scan-failed"
              />
              <StatTile
                label="Errored"
                value={data.outcomes.downloadFailed + data.outcomes.exception}
                tone="bad"
                icon={AlertTriangle}
                testid="stat-errored"
              />
              <StatTile
                label="Failures"
                value={failureCount}
                tone={failureCount === 0 ? "good" : "bad"}
                icon={AlertTriangle}
                testid="stat-failures"
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ListChecks className="h-4 w-4" />
                    Recent migration runs
                  </span>
                  {runsData ? (
                    <Badge
                      variant="outline"
                      data-testid="badge-runs-count"
                    >
                      {runsData.runs.length} run
                      {runsData.runs.length === 1 ? "" : "s"} ·{" "}
                      {runsData.rowsScanned.toLocaleString()} audit rows
                    </Badge>
                  ) : null}
                </CardTitle>
                <CardDescription>
                  Audit-log entries are clustered into runs using a{" "}
                  {runsData?.gapMinutes ?? 30}-minute idle gap. Expand a run
                  to see per-source counts and the{" "}
                  <code className="font-mono text-[10px]">
                    document_access_log
                  </code>{" "}
                  ids for any failures.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {runsLoading ? (
                  <div
                    className="flex items-center justify-center py-8 text-muted-foreground text-sm"
                    data-testid="text-runs-loading"
                  >
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading runs…
                  </div>
                ) : runsError ? (
                  <p
                    className="text-sm text-destructive"
                    data-testid="text-runs-error"
                  >
                    {(runsError as Error).message}
                  </p>
                ) : !runsData || runsData.runs.length === 0 ? (
                  <p
                    className="text-sm text-muted-foreground py-2"
                    data-testid="text-empty-runs"
                  >
                    No migration runs recorded yet.
                  </p>
                ) : (
                  <div className="space-y-2" data-testid="list-runs">
                    {runsData.runs.map((run) => (
                      <RunRow
                        key={run.runId}
                        run={run}
                        onSelectFailure={handleSelectFailure}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Per-workspace breakdown
                </CardTitle>
                <CardDescription>
                  Real (non-dry-run) outcomes grouped by workspace and source.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data.byWorkspace.length === 0 ? (
                  <p
                    className="text-sm text-muted-foreground"
                    data-testid="text-empty-workspaces"
                  >
                    No migration audit rows yet. Run{" "}
                    <span className="font-mono text-xs">
                      tsx scripts/encrypt-existing-attachments.ts
                    </span>{" "}
                    to populate this view.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {data.byWorkspace.map((ws) => (
                      <WorkspaceCard
                        key={ws.workspaceId}
                        summary={ws}
                        onRetryWorkspace={handleRetryWorkspace}
                        isRetryingWorkspace={
                          pendingWorkspaceId === ws.workspaceId &&
                          retryMutation.isPending
                        }
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    Failed migrations
                  </span>
                  <Badge variant="outline" data-testid="badge-failure-count">
                    Showing {data.failures.length} of latest {data.failureLimit}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Most-recent attempts that did not finish — drill down to the
                  originating row to clean up dangling references and retry
                  the file once the underlying issue is fixed.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data.failures.length === 0 ? (
                  <p
                    className="text-sm text-muted-foreground py-4"
                    data-testid="text-empty-failures"
                  >
                    No failed migrations recorded — the back-fill is clean.
                  </p>
                ) : (
                  <ScrollArea className="max-h-[480px]">
                    <div className="rounded-md border overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-muted/40 text-xs text-muted-foreground sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">
                              When / audit row
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                              Outcome
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                              Workspace
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                              File / object
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                              Originating row
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                              Threat / error
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.failures.map((row) => (
                            <FailureRowView
                              key={row.id}
                              row={row}
                              highlighted={highlightedFailureId === row.id}
                              onRetry={handleRetryRow}
                              isRetrying={
                                pendingFailureId === row.id &&
                                retryMutation.isPending
                              }
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </ScrollArea>
                )}
                {data.failures.length >= data.failureLimit ? (
                  <div className="mt-3 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFailureLimit((v) => Math.min(v + 200, 1000))}
                      disabled={failureLimit >= 1000 || isFetching}
                      data-testid="button-load-more-failures"
                    >
                      Load more (up to 1000)
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>
    </>
  );
}

export default function PlatformEncryptionMigration() {
  return <PlatformLayout><EncryptionMigrationContent /></PlatformLayout>;
}

